import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { THEME } from '../../utils/theme';
import api from '../../utils/api';
import Spinner from '../ui/Spinner';

const AddGameModal = ({ onClose, onGameAdded }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  const [adding, setAdding] = useState(false);
  const [message, setMessage] = useState(null);
  const [igdbConfigured, setIgdbConfigured] = useState(true);

  const searchGames = useCallback(async () => {
    if (!searchQuery.trim() || searchQuery.length < 2) return;
    setSearching(true);
    setMessage(null);
    try {
      const data = await api.get(`/api/integrations/igdb/search?q=${encodeURIComponent(searchQuery)}&limit=10`);
      if (!data.available) {
        setIgdbConfigured(false);
        setResults([]);
      } else {
        setResults(data.results || []);
        if (data.results?.length === 0) {
          setMessage({ type: 'info', text: 'No games found. Try a different search term.' });
        }
      }
    } catch (err) {
      console.error('IGDB search error:', err);
      setMessage({ type: 'error', text: 'Search failed. Please try again.' });
    } finally {
      setSearching(false);
    }
  }, [searchQuery]);

  const addGame = async (game) => {
    setAdding(true);
    setMessage(null);
    try {
      const result = await api.post('/api/integrations/igdb/add', {
        igdb_id: game.igdb_id,
        name: game.name,
        cover_url: game.cover_url,
        summary: game.summary,
        genres: game.genres,
        release_date: game.release_date
      });
      if (result.success) {
        setMessage({ type: 'success', text: result.message });
        setSelectedGame(null);
        if (onGameAdded) onGameAdded();
        // Auto-close after success
        setTimeout(() => onClose(), 1500);
      }
    } catch (err) {
      console.error('Add game error:', err);
      setMessage({ type: 'error', text: 'Failed to add game. Please try again.' });
    } finally {
      setAdding(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(5,5,10,0.88)', backdropFilter: 'blur(32px) saturate(180%)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        onClick={e => e.stopPropagation()}
        style={{ background: `linear-gradient(165deg, ${THEME.bgCard} 0%, ${THEME.bgElevated} 100%)`, borderRadius: THEME.radius.xl, width: '100%', maxWidth: '600px', maxHeight: '85vh', overflow: 'hidden', border: `1px solid ${THEME.glassBorder}`, boxShadow: THEME.modalShadow }}
      >
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${THEME.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: THEME.text }}>Add Non-Steam Game</h2>
          <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }} onClick={onClose} style={{ background: THEME.bgHover, border: 'none', borderRadius: '8px', width: '36px', height: '36px', cursor: 'pointer', color: THEME.textMuted, fontSize: '18px' }}>&times;</motion.button>
        </div>

        <div style={{ padding: '20px 24px', overflowY: 'auto', maxHeight: 'calc(85vh - 80px)' }}>
          {!igdbConfigured ? (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎮</div>
              <h3 style={{ color: THEME.text, marginBottom: '8px' }}>IGDB Not Configured</h3>
              <p style={{ color: THEME.textMuted, fontSize: '14px' }}>
                To add non-Steam games, configure TWITCH_CLIENT_ID and TWITCH_CLIENT_SECRET in the server settings.
              </p>
            </div>
          ) : (
            <>
              {/* Search Input */}
              <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && searchGames()}
                  placeholder="Search games on IGDB..."
                  autoFocus
                  style={{ flex: 1, background: THEME.bg, border: `1px solid ${THEME.border}`, borderRadius: '10px', padding: '12px 16px', color: THEME.text, fontSize: '14px', outline: 'none' }}
                />
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={searchGames}
                  disabled={searching || searchQuery.length < 2}
                  style={{ background: THEME.accent, color: '#000', border: 'none', borderRadius: '10px', padding: '12px 20px', fontWeight: '600', cursor: searching || searchQuery.length < 2 ? 'not-allowed' : 'pointer', opacity: searching || searchQuery.length < 2 ? 0.5 : 1 }}
                >
                  {searching ? <Spinner size={16} /> : 'Search'}
                </motion.button>
              </div>

              {/* Message */}
              {message && (
                <div style={{ padding: '12px 16px', borderRadius: '10px', marginBottom: '16px', background: message.type === 'success' ? `${THEME.success}20` : message.type === 'error' ? `${THEME.danger}20` : `${THEME.info}20`, color: message.type === 'success' ? THEME.success : message.type === 'error' ? THEME.danger : THEME.info, fontSize: '14px', fontWeight: '500' }}>
                  {message.text}
                </div>
              )}

              {/* Search Results */}
              {results.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {results.map(game => (
                    <motion.div
                      key={game.igdb_id}
                      whileHover={{ scale: 1.01 }}
                      style={{ display: 'flex', gap: '14px', padding: '14px', background: selectedGame?.igdb_id === game.igdb_id ? `${THEME.accent}15` : THEME.bg, borderRadius: '12px', border: `1px solid ${selectedGame?.igdb_id === game.igdb_id ? THEME.accent : THEME.border}`, cursor: 'pointer', transition: 'all 0.15s ease' }}
                      onClick={() => setSelectedGame(game)}
                    >
                      {/* Cover Art */}
                      {game.cover_url ? (
                        <img src={game.cover_url} alt="" style={{ width: '60px', height: '80px', objectFit: 'cover', borderRadius: '8px', flexShrink: 0 }} />
                      ) : (
                        <div style={{ width: '60px', height: '80px', background: THEME.bgHover, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: THEME.textMuted, fontSize: '24px', flexShrink: 0 }}>🎮</div>
                      )}

                      {/* Game Info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h4 style={{ margin: '0 0 6px 0', fontSize: '15px', fontWeight: '600', color: THEME.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{game.name}</h4>
                        {game.release_date && (
                          <div style={{ fontSize: '12px', color: THEME.textMuted, marginBottom: '6px' }}>
                            {new Date(game.release_date * 1000).getFullYear()}
                          </div>
                        )}
                        {game.genres?.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                            {game.genres.slice(0, 3).map((g, i) => (
                              <span key={i} style={{ background: THEME.bgHover, padding: '2px 8px', borderRadius: '4px', fontSize: '11px', color: THEME.textMuted }}>{g}</span>
                            ))}
                          </div>
                        )}
                        {game.rating && (
                          <div style={{ marginTop: '6px', fontSize: '12px', color: THEME.success }}>
                            ★ {game.rating}/100
                          </div>
                        )}
                      </div>

                      {/* Add Button */}
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={(e) => { e.stopPropagation(); addGame(game); }}
                        disabled={adding}
                        style={{ alignSelf: 'center', background: `linear-gradient(135deg, ${THEME.accent}, ${THEME.supe})`, color: '#000', border: 'none', borderRadius: '8px', padding: '8px 16px', fontWeight: '600', fontSize: '13px', cursor: adding ? 'not-allowed' : 'pointer', opacity: adding ? 0.5 : 1, flexShrink: 0 }}
                      >
                        {adding ? <Spinner size={14} /> : '+ Add'}
                      </motion.button>
                    </motion.div>
                  ))}
                </div>
              )}

              {/* Empty State */}
              {!searching && results.length === 0 && !message && (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: THEME.textMuted }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
                  <p style={{ fontSize: '14px' }}>Search for games on IGDB to add them to your library</p>
                  <p style={{ fontSize: '12px', marginTop: '8px', opacity: 0.7 }}>Perfect for non-Steam games, console games, or games not in your Steam library</p>
                </div>
              )}
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AddGameModal;
